/**
 * BusinessProcessForm Component
 *
 * Reusable form for creating and editing business processes
 */

import {
  TextField,
  SelectField,
  EntityAutocompleteField,
  type CreateBusinessProcessFormData,
} from '../forms';
import { createBusinessProcessSchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { TagPicker } from '../tags/TagPicker';
import { useAllStakeholders, useBusinessUnits } from '../../graphql/queries';
import '@tylertech/forge/card';
import '@tylertech/forge/icon';
import './BusinessProcessForm.css';

export interface BusinessProcessFormProps {
  /** Initial form values (for edit mode) */
  defaultValues?: Partial<CreateBusinessProcessFormData>;
  /** Submit handler */
  onSubmit: (data: CreateBusinessProcessFormData) => void | Promise<void>;
  /** Cancel handler */
  onCancel: () => void;
  /** Is form submitting */
  isSubmitting?: boolean;
  /** Form mode */
  mode: 'create' | 'edit';
}

const CardElement = 'forge-card' as React.ElementType;

/**
 * BusinessProcess create/edit form with validation
 *
 * @example
 * ```tsx
 * <BusinessProcessForm
 *   mode="create"
 *   onSubmit={handleCreate}
 *   onCancel={() => navigate('/business-processes')}
 * />
 * ```
 */
export function BusinessProcessForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: BusinessProcessFormProps): JSX.Element {
  // Stakeholders and business units are broadly readable (GraphQL reads have
  // no per-entity role gate at all — see `canView` in
  // `backend/src/middleware/permissions.ts`), unlike the admin-gated REST
  // `/users` endpoint, so both pickers source from GraphQL catalogs a
  // BusinessEditor can always read (issue #769).
  const { data: stakeholdersData } = useAllStakeholders();
  const stakeholderOptions = (stakeholdersData?.stakeholders?.edges ?? []).map(({ node }) => ({
    value: node.entityId,
    label: node.name,
  }));

  const { data: businessUnitsData } = useBusinessUnits();
  const businessUnitOptions = (businessUnitsData?.businessUnits?.edges ?? []).map(({ node }) => ({
    value: node.entityId,
    label: node.name,
  }));

  return (
    <EntityForm<CreateBusinessProcessFormData>
      schema={createBusinessProcessSchema}
      baseValues={{
        processType: 'core',
        processTier: 'operational',
        automationLevel: 'manual',
        status: 'draft',
        tags: [],
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Create Business Process"
      formClassName="business-process-form"
    >
      {({ register, formState: { errors }, values, setValue }) => {
        const tags = values.tags || [];

        return (
          <>
            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Basic Information</h2>

                <TextField
                  label="Name"
                  {...register('name')}
                  value={values.name || ''}
                  error={errors.name}
                  required
                  placeholder="e.g., Customer Onboarding"
                  helperText="A descriptive name for the business process"
                />

                <TextField
                  label="Description"
                  {...register('description')}
                  value={values.description || ''}
                  error={errors.description}
                  placeholder="Brief description of the business process"
                  helperText="Optional description (max 2000 characters)"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Classification</h2>

                <SelectField
                  label="Process Type"
                  {...register('processType')}
                  value={values.processType}
                  options={[
                    { value: 'core', label: 'Core' },
                    { value: 'supporting', label: 'Supporting' },
                    { value: 'management', label: 'Management' },
                  ]}
                  error={errors.processType}
                  required
                />

                <SelectField
                  label="Process Tier"
                  {...register('processTier')}
                  value={values.processTier}
                  options={[
                    { value: 'strategic', label: 'Strategic' },
                    { value: 'operational', label: 'Operational' },
                    { value: 'tactical', label: 'Tactical' },
                  ]}
                  error={errors.processTier}
                  required
                />

                <SelectField
                  label="Automation Level"
                  {...register('automationLevel')}
                  value={values.automationLevel || ''}
                  options={[
                    { value: 'manual', label: 'Manual' },
                    { value: 'semi-automated', label: 'Semi-Automated' },
                    { value: 'automated', label: 'Automated' },
                    { value: 'autonomous', label: 'Autonomous' },
                  ]}
                  error={errors.automationLevel}
                />

                <SelectField
                  label="Status"
                  {...register('status')}
                  value={values.status || ''}
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'active', label: 'Active' },
                    { value: 'deprecated', label: 'Deprecated' },
                    { value: 'retired', label: 'Retired' },
                  ]}
                  error={errors.status}
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Details</h2>

                <EntityAutocompleteField
                  name="processOwnerId"
                  label="Process Owner"
                  options={stakeholderOptions}
                  value={values.processOwnerId || ''}
                  onChange={(entityId) =>
                    setValue('processOwnerId', entityId, { shouldValidate: true })
                  }
                  error={errors.processOwnerId}
                  required
                  placeholder="Search stakeholders…"
                  helperText="The stakeholder responsible for this process"
                />

                <EntityAutocompleteField
                  name="owningBusinessUnitId"
                  label="Owning Business Unit"
                  options={businessUnitOptions}
                  value={values.owningBusinessUnitId || ''}
                  onChange={(entityId) =>
                    setValue('owningBusinessUnitId', entityId, { shouldValidate: true })
                  }
                  error={errors.owningBusinessUnitId}
                  required
                  placeholder="Search business units…"
                  helperText="The business unit that owns this process"
                />

                <TextField
                  label="Estimated Duration"
                  {...register('estimatedDuration')}
                  value={values.estimatedDuration || ''}
                  error={errors.estimatedDuration}
                  placeholder="e.g., 2 hours"
                  helperText="Optional estimated duration"
                />

                <TextField
                  label="Volume Per Period"
                  {...register('volumePerPeriod')}
                  value={values.volumePerPeriod || ''}
                  error={errors.volumePerPeriod}
                  placeholder="e.g., 500/month"
                  helperText="Optional volume per period"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Descriptions</h2>

                <TextField
                  label="Input Description"
                  {...register('inputDescription')}
                  value={values.inputDescription || ''}
                  error={errors.inputDescription}
                  placeholder="Describe the inputs to this process"
                  helperText="Optional description of process inputs"
                />

                <TextField
                  label="Output Description"
                  {...register('outputDescription')}
                  value={values.outputDescription || ''}
                  error={errors.outputDescription}
                  placeholder="Describe the outputs of this process"
                  helperText="Optional description of process outputs"
                />

                <TextField
                  label="Tooling Description"
                  {...register('toolingDescription')}
                  value={values.toolingDescription || ''}
                  error={errors.toolingDescription}
                  placeholder="Describe the tools used in this process"
                  helperText="Optional description of tooling"
                />
              </div>
            </CardElement>

            <CardElement className="form-card">
              <div className="form-section">
                <h2 className="forge-typography--headline6">Additional Information</h2>

                <TagPicker
                  id="business-process-tag-picker"
                  label="Tags"
                  selectedTagIds={tags}
                  onChange={(newIds) => setValue('tags', newIds)}
                />

                <TextField
                  label="Notes"
                  {...register('notes')}
                  value={values.notes || ''}
                  error={errors.notes}
                  placeholder="Additional notes about this business process"
                  helperText="Optional notes (max 5000 characters)"
                />
              </div>
            </CardElement>
          </>
        );
      }}
    </EntityForm>
  );
}
